import React, { useState, useEffect } from 'react';
import { useShop } from '../../context/ShopContext';
import { api } from '../../services/api';
import {
  Database,
  Server,
  Cloud,
  HardDrive,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Activity,
  Sparkles,
  ShieldCheck,
  Gem,
  ShoppingBag,
  Users,
  Tag,
  TrendingUp,
  Download,
  Info,
  Check,
  Copy,
  Terminal,
  Layers
} from 'lucide-react';

export default function AdminDatabaseHub() {
  const {
    dbStatus,
    syncDatabase,
    isSyncingDb,
    products,
    orders,
    consultations,
    coupons,
    liveGoldRates,
    exportStoreBackup,
    addToast
  } = useShop();

  const [detailedStatus, setDetailedStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [pingTime, setPingTime] = useState(null);
  const [isPinging, setIsPinging] = useState(false);

  // Connection form state
  const [targetUri, setTargetUri] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [persistUri, setPersistUri] = useState(true);
  const [connectMsg, setConnectMsg] = useState(null);

  // Seeding state
  const [isSeeding, setIsSeeding] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);

  const fetchDetailedInfo = async () => {
    setIsLoadingStatus(true);
    try {
      const data = await api.getDbDetailedStatus();
      if (data) {
        setDetailedStatus(data);
        if (data.database?.latency) setPingTime(data.database.latency);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchDetailedInfo();
  }, [dbStatus]);

  const handlePing = async () => {
    setIsPinging(true);
    const start = performance.now();
    try {
      const res = await api.pingDatabase();
      const latency = Math.round(performance.now() - start);
      setPingTime(latency);
      addToast(`API & Database Ping: ${latency}ms latency`, 'success');
    } catch {
      addToast('Ping failed: Server is unreachable', 'error');
    } finally {
      setIsPinging(false);
    }
  };

  const handleConnect = async (e) => {
    e?.preventDefault();
    if (!targetUri.trim()) {
      addToast('Please enter a MongoDB connection URI or choose a preset', 'error');
      return;
    }

    setIsConnecting(true);
    setConnectMsg(null);
    try {
      const res = await api.connectDatabase(targetUri.trim(), persistUri);
      if (res.success) {
        setConnectMsg({ type: 'success', text: res.message || 'Connected successfully!' });
        addToast(res.message || 'Database connected live!', 'success');
        await syncDatabase(true);
        await fetchDetailedInfo();
      } else {
        setConnectMsg({ type: 'error', text: res.error || res.message || 'Failed to connect' });
        addToast(res.error || 'Connection failed', 'error');
      }
    } catch (err) {
      setConnectMsg({ type: 'error', text: err.message });
      addToast(`Connection error: ${err.message}`, 'error');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleReseed = async () => {
    if (!window.confirm('Re-seed Royal Catalog? This will populate all initial hallmarked jewellery, categories, and gold rates into your active database (MySQL / MongoDB).')) {
      return;
    }
    setIsSeeding(true);
    try {
      let res = await api.seedMysqlDatabase();
      if (!res.success && api.seedDatabase) {
        res = await api.seedDatabase();
      }
      if (res.success) {
        addToast(res.message || 'Database seeded successfully with royal catalog!', 'success');
        await syncDatabase(false);
        await fetchDetailedInfo();
      } else {
        addToast(res.message || res.error || 'Reseed failed', 'error');
      }
    } catch (err) {
      addToast(`Reseed error: ${err.message}`, 'error');
    } finally {
      setIsSeeding(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedScript(true);
    setTimeout(() => setCopiedScript(false), 2500);
    addToast('Copied to clipboard!', 'info');
  };

  const isLive = dbStatus?.isConnected;
  const dbMode = dbStatus?.mode || 'offline';

  const modeBadgeText = {
    mysql: 'MySQL / MariaDB (XAMPP / Hostinger)',
    mysql_php: 'MySQL / MariaDB (XAMPP / Hostinger)',
    atlas: 'MongoDB Atlas Cloud (Production)',
    local: 'Local MongoDB Service (Port 27017)',
    memory: 'Embedded In-Memory MongoDB Engine (Zero Setup)',
    offline: 'Local Storage Vault (Offline Fallback)'
  }[dbMode] || dbStatus?.engine || dbMode;

  return (
    <div style={{ maxWidth: '1050px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(20, 20, 32, 0.95), rgba(12, 12, 20, 0.95))',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-md)',
        padding: '1.8rem 2rem',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1.2rem'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', color: 'var(--gold-400)', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.4rem' }}>
            <Database size={15} /> Database & Cloud Architecture
          </div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff', margin: 0 }}>
            Real-time Database <span className="text-gold-gradient">Command Center</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0.4rem 0 0 0', maxWidth: '600px' }}>
            Manage database connections, monitor real-time latency, switch between MongoDB Atlas Cloud and Local engines, and synchronize catalog records.
          </p>
        </div>

        {/* Live Status Pill & Ping */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            padding: '0.55rem 1.1rem',
            borderRadius: 'var(--radius-full)',
            background: isLive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${isLive ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
            color: isLive ? '#34d399' : '#f87171',
            fontSize: '0.88rem',
            fontWeight: 700
          }}>
            <span style={{
              width: '9px',
              height: '9px',
              borderRadius: '50%',
              background: isLive ? '#10b981' : '#ef4444',
              boxShadow: isLive ? '0 0 10px #10b981' : 'none'
            }} />
            <span>{isLive ? 'CONNECTED & LIVE' : 'OFFLINE / DISCONNECTED'}</span>
          </div>

          <button
            type="button"
            className="btn btn-glass btn-sm"
            onClick={handlePing}
            disabled={isPinging}
            style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8rem' }}
          >
            <Activity size={14} style={{ animation: isPinging ? 'spin 1s linear infinite' : 'none' }} />
            <span>{isPinging ? 'Pinging...' : pingTime ? `${pingTime}ms Latency` : 'Test Ping'}</span>
          </button>
        </div>
      </div>

      {/* Top Diagnostics Card Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.2rem' }}>
        
        {/* Card 1: Active Engine */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.4rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Active Database Engine
            </span>
            {dbMode === 'atlas' ? <Cloud size={18} color="var(--gold-400)" /> : <HardDrive size={18} color="var(--gold-400)" />}
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', marginBottom: '0.3rem' }}>
            {modeBadgeText}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--gold-300)', fontFamily: 'monospace' }}>
            {dbStatus?.uri ? `${dbStatus.uri}` : 'Offline Local Vault Storage'}
          </div>
        </div>

        {/* Card 2: Host & Target DB */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.4rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Host & Database
            </span>
            <Server size={18} color="var(--gold-400)" />
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', marginBottom: '0.3rem' }}>
            Database: <span className="text-gold-gradient">{dbStatus?.name || 'mljewellers'}</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            Host: {dbStatus?.host || 'localhost'} (Ready State: {dbStatus?.readyStateText || (isLive ? 'connected' : 'offline')})
          </div>
        </div>

        {/* Card 3: Realtime Synchronization */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.4rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Real-time Vault Sync
            </span>
            <RefreshCw size={18} color="var(--gold-400)" style={{ animation: isSyncingDb ? 'spin 1s linear infinite' : 'none' }} />
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', marginBottom: '0.6rem' }}>
            {isSyncingDb ? 'Syncing...' : 'All Vault Collections Synced'}
          </div>
          <button
            type="button"
            className="btn btn-gold btn-sm"
            onClick={() => syncDatabase(false)}
            disabled={isSyncingDb}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', fontSize: '0.8rem' }}
          >
            <RefreshCw size={13} style={{ animation: isSyncingDb ? 'spin 1s linear infinite' : 'none' }} />
            <span>{isSyncingDb ? 'Synchronizing...' : 'Sync All Collections Now'}</span>
          </button>
        </div>

      </div>

      {/* Collection Counter Metric Badges */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '1.8rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '0.8rem' }}>
          <div>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
              Live Collections & Inventory Telemetry
            </h3>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Total record counts currently active in the vault database.
            </span>
          </div>
          <button
            type="button"
            className="btn btn-glass btn-sm"
            onClick={fetchDetailedInfo}
            disabled={isLoadingStatus}
            style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
          >
            <RefreshCw size={13} style={{ animation: isLoadingStatus ? 'spin 1s linear infinite' : 'none' }} />
            <span>Refresh Telemetry</span>
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
          
          <div style={{ background: '#0a0a12', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '1rem', textAlign: 'center' }}>
            <Gem size={20} color="var(--gold-400)" style={{ margin: '0 auto 0.4rem' }} />
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
              {detailedStatus?.counts?.products ?? products?.length ?? 0}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Products
            </div>
          </div>

          <div style={{ background: '#0a0a12', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '1rem', textAlign: 'center' }}>
            <ShoppingBag size={20} color="#38bdf8" style={{ margin: '0 auto 0.4rem' }} />
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
              {detailedStatus?.counts?.orders ?? orders?.length ?? 0}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Vault Orders
            </div>
          </div>

          <div style={{ background: '#0a0a12', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '1rem', textAlign: 'center' }}>
            <Users size={20} color="#a855f7" style={{ margin: '0 auto 0.4rem' }} />
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
              {detailedStatus?.counts?.inquiries ?? consultations?.length ?? 0}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              VIP Consultations
            </div>
          </div>

          <div style={{ background: '#0a0a12', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '1rem', textAlign: 'center' }}>
            <Tag size={20} color="#f59e0b" style={{ margin: '0 auto 0.4rem' }} />
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
              {detailedStatus?.counts?.coupons ?? coupons?.length ?? 0}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Active Coupons
            </div>
          </div>

          <div style={{ background: '#0a0a12', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '1rem', textAlign: 'center' }}>
            <TrendingUp size={20} color="#10b981" style={{ margin: '0 auto 0.4rem' }} />
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#34d399' }}>
              24K: ₹{(Number(liveGoldRates?.gold24K || liveGoldRates?.gold24k) > 20000 ? Math.round(Number(liveGoldRates?.gold24K || liveGoldRates?.gold24k) / 10) : Number(liveGoldRates?.gold24K || liveGoldRates?.gold24k || 7425)).toLocaleString('en-IN')}/g
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Gold Spot Bullion
            </div>
          </div>

          <div style={{ background: '#0a0a12', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '1rem', textAlign: 'center' }}>
            <Layers size={20} color="var(--gold-300)" style={{ margin: '0 auto 0.4rem' }} />
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
              Active
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              CMS Visuals
            </div>
          </div>

        </div>
      </div>

      {/* Database Connection Switcher Form */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
          <Zap size={20} color="var(--gold-400)" />
          <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: 0 }}>
            Connect Custom Database / MongoDB Atlas Cloud
          </h3>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
          Paste your MongoDB Atlas connection string to connect permanently to the cloud, or switch between local database engines with zero downtime.
        </p>

        {/* Preset Quick Buttons */}
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
          <button
            type="button"
            className="btn btn-glass btn-sm"
            onClick={() => setTargetUri('mongodb://localhost:27017/mljewellers')}
            style={{ fontSize: '0.78rem' }}
          >
            <HardDrive size={13} /> Local MongoDB (localhost:27017)
          </button>
          <button
            type="button"
            className="btn btn-glass btn-sm"
            onClick={() => setTargetUri('memory')}
            style={{ fontSize: '0.78rem' }}
          >
            <Zap size={13} /> Embedded In-Memory Engine (Zero Setup)
          </button>
          <button
            type="button"
            className="btn btn-glass btn-sm"
            onClick={() => setTargetUri('mongodb+srv://<username>:<password>@cluster0.mongodb.net/mljewellers?retryWrites=true&w=majority')}
            style={{ fontSize: '0.78rem' }}
          >
            <Cloud size={13} /> MongoDB Atlas Template
          </button>
        </div>

        <form onSubmit={handleConnect} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
              MongoDB Connection URI *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. mongodb+srv://admin:pass@cluster0.abcde.mongodb.net/mljewellers?retryWrites=true&w=majority or 'memory'"
              value={targetUri}
              onChange={(e) => setTargetUri(e.target.value)}
              className="input-luxury"
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <input
              type="checkbox"
              id="persistUri"
              checked={persistUri}
              onChange={(e) => setPersistUri(e.target.checked)}
              style={{ accentColor: 'var(--gold-400)', cursor: 'pointer' }}
            />
            <label htmlFor="persistUri" style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              Persist this connection string in <code style={{ color: 'var(--gold-300)' }}>server/.env</code> across server restarts
            </label>
          </div>

          {connectMsg && (
            <div style={{
              padding: '0.8rem 1rem',
              borderRadius: 'var(--radius-sm)',
              background: connectMsg.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              border: `1px solid ${connectMsg.type === 'success' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
              color: connectMsg.type === 'success' ? '#34d399' : '#f87171',
              fontSize: '0.84rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {connectMsg.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              <span>{connectMsg.text}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              type="submit"
              className="btn btn-gold"
              disabled={isConnecting}
              style={{
                padding: '0.75rem 1.8rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.88rem',
                fontWeight: 700
              }}
            >
              <Zap size={16} style={{ animation: isConnecting ? 'spin 1s linear infinite' : 'none' }} />
              <span>{isConnecting ? 'Connecting to Database...' : 'Test & Connect Live'}</span>
            </button>

            <button
              type="button"
              className="btn btn-glass"
              onClick={handleReseed}
              disabled={isSeeding}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.88rem',
                color: 'var(--gold-300)',
                borderColor: 'var(--border-gold)'
              }}
            >
              <Sparkles size={16} style={{ animation: isSeeding ? 'spin 1s linear infinite' : 'none' }} />
              <span>{isSeeding ? 'Seeding Catalog...' : 'Re-Seed Royal Catalog Data'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Dual Stack: XAMPP & MySQL Support Information */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '1.8rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
          <Server size={20} color="var(--gold-400)" />
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
            Dual-Stack Support: XAMPP Apache + MySQL / phpMyAdmin
          </h3>
        </div>
        <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '1.2rem' }}>
          This project also includes a ready-to-run PHP/MySQL schema inside the <code style={{ color: 'var(--gold-300)' }}>api/</code> folder for XAMPP environments:
        </p>

        <div style={{
          background: '#0a0a12',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          borderRadius: 'var(--radius-sm)',
          padding: '1rem',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          color: 'var(--gold-200)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <strong>MySQL Database:</strong> mljewellers_db (XAMPP default port 3306, user: root, password: empty)<br />
            <strong>Schema File:</strong> api/schema.sql<br />
            <strong>PHP API Config:</strong> api/config.php
          </div>
          <button
            type="button"
            className="btn btn-glass btn-sm"
            onClick={() => copyToClipboard('C:\\xampp\\htdocs\\projects\\mljewellersandkitchenware.com\\api\\schema.sql')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem' }}
          >
            {copiedScript ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
            <span>{copiedScript ? 'Copied' : 'Copy Path'}</span>
          </button>
        </div>
      </div>

    </div>
  );
}
